import { DecisionTreeActionType } from 'redux/actions/decisionTreeActions';
import {
  ADD_STEP_TO_CURRENT_DT_PATH,
  CREATE_CASE_END,
  GET_FIRST_DECISION_TREE_STEP,
  GET_ICVC_POLICIES_START,
  GET_ICVC_POLICIES_SUCCESS,
  GET_QUESTIONS_DECISION_TREE_SUCCESS,
  PREPARE_NEW_DECISION_TREE_SERVICE,
  REMOVE_STEP_FROM_CURRENT_DT_PATH,
  RESTORE_LAST_CC_DECISION_TREE,
  UPDATE_CHECK_UP_SERVICES,
  UPDATE_DECISION_TREE_SERVICES,
} from 'redux/actions/types';
import {
  DecisionTreeType,
  LocalDecisionTreeState,
  LocalServiceTree,
  ResponseQuestionsType,
} from 'ts/types/decision-tree.types';
import { CONTEXT_QUESTIONS } from 'constants/computableContracts';
import { ICVCActionType } from 'redux/actions/icvcActions';
import { ICVCResponse } from 'ts/types';

export const initState: LocalDecisionTreeState = {
  diagnosis: {
    result: {},
    actualPath: [],
  },
  services: {},
  currentCheckUpService: undefined,
  currentServices: [],
  currentService: '',
  currentDT: DecisionTreeType.DIAGNOSIS,
  currentPath: [],
  lastAnswers: {},
  interventionObjectsContext: [],
  packageOptions: [],
};

const addNewStep = (
  state: LocalDecisionTreeState,
  payload: {
    answer: string;
    displayName: string;
    question: string;
    questionText: string;
    response: ResponseQuestionsType;
  },
) => {
  const { currentDT, currentPath: previousPath, currentService } = state;
  const {
    response: { coverageResponses, nextQuestion },
    answer,
    displayName,
    question,
    questionText,
  } = payload;
  const newNode = { coverageResponses, nextQuestion };
  let tempTree = JSON.parse(JSON.stringify(state[currentDT]));
  let currentTree =
    currentDT === DecisionTreeType.SERVICE
      ? tempTree[currentService]
      : tempTree;
  const currentQuestionIndex = previousPath.findIndex(
    ({ id }) => id === question,
  );
  let currentPath =
    currentQuestionIndex >= 0
      ? previousPath.slice(0, currentQuestionIndex)
      : previousPath;

  const totalContextQuestions = CONTEXT_QUESTIONS.concat(state.packageOptions);

  if (
    !(
      totalContextQuestions.includes(question) ||
      totalContextQuestions.includes(questionText)
    )
  ) {
    currentPath.forEach(({ value }: { value: string }) => {
      currentTree = currentTree.givenAnswers[value];
    });
    currentTree.givenAnswers = {
      ...(currentTree.givenAnswers || {}),
      [answer]: newNode,
    };
    const { id } = currentTree.nextQuestion;
    currentPath = [
      ...currentPath,
      { id, value: answer, questionText, answerText: displayName },
    ];
  } else if (currentDT === DecisionTreeType.DIAGNOSIS) {
    tempTree = { ...tempTree, ...newNode };
  } else {
    tempTree[currentService] = { ...currentTree, ...newNode };
  }

  return {
    ...state,
    currentPath,
    [currentDT]: tempTree,
    lastAnswers: { ...state.lastAnswers, [question]: answer },
  };
};

const extractPackageOptions = (payload: ICVCResponse) => {
  const policyStartDate = payload.policy_information?.effective_period
    ?.start_date
    ? ['POLICY_START_DATE']
    : [];
  const packageOptions =
    payload.policy_information?.package?.options?.map(
      ({ option_reference }: { option_reference: string }) => option_reference,
    ) || [];
  return policyStartDate.concat(packageOptions);
};

function decisionTreeCurrentStepReducer(
  state: LocalDecisionTreeState = initState,
  action: DecisionTreeActionType | ICVCActionType,
): any {
  let previousService: string;
  switch (action.type) {
    case GET_ICVC_POLICIES_START:
      return {
        ...state,
        packageOptions: [],
      };
    case GET_ICVC_POLICIES_SUCCESS:
      const packageOptions = extractPackageOptions(action.payload);
      return {
        ...state,
        packageOptions,
      };
    case GET_QUESTIONS_DECISION_TREE_SUCCESS:
      return addNewStep(state, action.payload);
    case ADD_STEP_TO_CURRENT_DT_PATH:
      return {
        ...state,
        currentPath: [...state.currentPath, action.payload],
        lastAnswers: {
          ...state.lastAnswers,
          [action.payload.id]: action.payload.value,
        },
      };
    case REMOVE_STEP_FROM_CURRENT_DT_PATH:
      return { ...state, currentPath: state.currentPath.slice(0, -1) };
    case PREPARE_NEW_DECISION_TREE_SERVICE:
      previousService = state.currentService;
      const { questions, service } = action.payload;
      const currentService = service || previousService;
      return {
        ...state,
        currentService,
        currentPath:
          currentService !== previousService
            ? []
            : state.currentPath.slice(0, -1),
        services: {
          ...state.services,
          [previousService]: {
            ...state.services[previousService],
            actualPath: state.currentPath,
            questions,
          },
        },
      };
    case RESTORE_LAST_CC_DECISION_TREE:
      const currentServiceIndex = state.currentServices.findIndex(
        service => service === state.currentService,
      );
      if (currentServiceIndex > 0) {
        const newCurrentService =
          state.currentServices[currentServiceIndex - 1];
        return {
          ...state,
          currentService: newCurrentService,
          currentPath: state.services[newCurrentService].actualPath,
          services: {
            ...state.services,
            [state.currentService]: {
              ...state.services[state.currentService],
              actualPath: state.currentPath,
            },
          },
        };
      } else {
        return {
          ...state,
          currentService: '',
          currentPath: state.diagnosis.actualPath,
          currentDT: DecisionTreeType.DIAGNOSIS,
          services: {
            ...state.services,
            [state.currentService]: {
              ...state.services[state.currentService],
              actualPath: state.currentPath,
            },
          },
        };
      }
    case UPDATE_DECISION_TREE_SERVICES:
      let serviceData = {};
      if (action.payload.services.length) {
        serviceData = {
          currentServices: Array.from(new Set(action.payload.services)),
          currentDT: DecisionTreeType.SERVICE,
          currentService: action.payload.services[0],
          currentPath: [],
          interventionObjectsContext: action.payload.interventionObjectsContext,
          services: action.payload.services.reduce(
            (acc: { [_: string]: LocalServiceTree }, cur: string) => ({
              ...acc,
              [cur]: state.services[cur] || {},
            }),
            state.services,
          ),
        };
      }
      return {
        ...state,
        ...serviceData,
        diagnosis: {
          ...state.diagnosis,
          actualPath: state.currentPath,
          result: action.payload.result,
        },
      };
    case UPDATE_CHECK_UP_SERVICES:
      return {
        ...state,
        currentCheckUpService: action.payload.service,
      };
    case GET_FIRST_DECISION_TREE_STEP:
      return {
        ...state,
        currentService: '',
        currentDT: DecisionTreeType.DIAGNOSIS,
        currentPath: [],
      };
    case CREATE_CASE_END:
      return initState;
    default:
      return state;
  }
}

export default decisionTreeCurrentStepReducer;
